<template>
  <div class="view view-workspace">
    <div class="view-workspace__sidebar">
      <manager-sidebar
        :organization-slug="organizationSlug"
        :team-slug="teamSlug"
        :folder-slug="folderSlug"
      >
        <workspace-view-nav
          :organization-slug="organizationSlug"
          :team-slug="teamSlug"
          :folder-slug="folderSlug"
        />
      </manager-sidebar>
    </div>

    <div class="view-workspace__main">
      <template v-if="context.currentWorkspace">
        <router-view></router-view>
      </template>
    </div>

    <!-- TEAM SIDEBAR -->
    <aside
      v-if="context.currentWorkspace && !context.currentWorkspace?.isPersonal"
      class="view-workspace__context"
    >
      <div>
        <h2>{{ $t("headings.members") }}</h2>
        <div class="view-workspace__members">
          <user-avatar
            v-for="member in activeWorkspaceMembers"
            :key="member.id"
            :user="member"
          />
        </div>
      </div>

      <div>
        <router-link
          v-if="context.currentWorkspace.membership"
          :to="{ name: 'settings-team' }"
          class="button button_secondary"
        >
          {{ $t("links.workspaceSettings") }}
        </router-link>
      </div>
    </aside>

    <SynchStatus />
  </div>
</template>

<script setup lang="ts">
/**
 * Workspace Overview
 *
 * This view shows the content of the current workspace.
 * The current workspace is set in the router.
 */

import { reactive, onMounted, computed } from "vue";
import { useWorkspaces } from "@/api/workspaces.store";

import WorkspaceViewNav from "./WorkspaceViewNav.vue";
import UserAvatar from "@manager/common/UserAvatar.vue";
import ManagerSidebar from "@manager/common/ManagerSidebar.vue";
import SynchStatus from "./SynchStatus.vue";

const props = withDefaults(
  defineProps<{
    organizationSlug?: string;
    teamSlug?: string;
    folderSlug?: string;
    modelId?: number | string;
  }>(),
  {
    organizationSlug: undefined,
    teamSlug: undefined,
    folderSlug: undefined,
    modelId: undefined
  }
);

const context = reactive(useWorkspaces(props));

const activeWorkspaceMembers = computed(() => {
  const members = context.currentWorkspace?.members ?? [];
  return members.filter((m) => m.accepted);
});

onMounted(() => {
  if (context.currentWorkspace) {
    if (
      activeWorkspaceMembers.value.length == 0 &&
      !context.currentWorkspace.isPersonal
    ) {
      context.getTeamMembers(context.currentWorkspace);
    }
  }
});
</script>

<style lang="scss">
.view-workspace {
  display: flex;

  &__sidebar {
    width: 20rem;
    flex-shrink: 0;
  }

  &__main {
    width: 100%;
    overflow: auto;
    padding: 2rem;
    padding-top: 3rem;
    min-width: 0; // makes sure flex resizing works
    scrollbar-gutter: stable;
  }

  &__breadcrumps {
    display: flex;
  }

  &__context {
    background-color: var(--color-background-deep);
    padding: 0.5rem 1rem;
    box-shadow: inset 5px 0 5px -5px var(--color-shadow-1);
    width: 20rem;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr) min-content;
  }
  &__members {
    display: flex;
    flex-wrap: wrap;
  }
}
</style>
